import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  PlusCircle, 
  Calendar, 
  Award, 
  ChevronDown, 
  ChevronUp,
  X
} from 'lucide-react';
import StatCard from '../shared/StatCard';
import { usePlayer } from '../../context/PlayerContext';

const GettingStartedCard = () => {
  const { playerData } = usePlayer();
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  
  // Check if this is a new user (player created in the last 24 hours)
  const [isNewUser, setIsNewUser] = useState(false);
  
  useEffect(() => {
    // Check if player was recently created
    const playerCreatedAt = localStorage.getItem('playerCreatedAt');
    if (playerCreatedAt) {
      const createdDate = new Date(playerCreatedAt);
      const now = new Date();
      const hoursSinceCreation = (now - createdDate) / (1000 * 60 * 60);
      
      // Consider as new user if account was created in the last 24 hours
      setIsNewUser(hoursSinceCreation < 24);
    }
    
    // Check if card was previously dismissed
    const cardDismissed = localStorage.getItem('gettingStartedDismissed');
    if (cardDismissed === 'true') {
      setDismissed(true);
    }
  }, []);
  
  // Dismiss the card permanently
  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('gettingStartedDismissed', 'true');
  };
  
  // If card is dismissed or not a new user, don't show it
  if (dismissed || !isNewUser) {
    return null;
  }
  
  // Check completed steps
  const hasGames = playerData?.games?.length > 0;
  const hasSchedule = playerData?.schedule?.length > 0;
  const hasMilestones = playerData?.milestones?.length > 0;
  
  return (
    <StatCard className="relative overflow-hidden border-l-4 border-blue-500">
      {/* Dismiss button */}
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
        aria-label="Dismiss getting started guide"
      >
        <X size={18} />
      </button>
      
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-medium text-lg">Getting Started Guide</h3>
        <button 
          onClick={() => setExpanded(!expanded)}
          className="text-blue-600 flex items-center text-sm"
        >
          {expanded ? (
            <>Hide <ChevronUp size={16} className="ml-1" /></>
          ) : (
            <>Show <ChevronDown size={16} className="ml-1" /></>
          )}
        </button>
      </div>
      
      {expanded && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Welcome to JStats! Here are a few steps to get you started:
          </p>
          
          <div className="space-y-3">
            {/* Add Game */}
            <div className="flex items-start">
              <div className={`flex-shrink-0 rounded-full p-1 ${hasGames ? 'bg-green-100' : 'bg-blue-100'} mr-3`}>
                {hasGames ? (
                  <CheckCircle size={18} className="text-green-600" />
                ) : (
                  <PlusCircle size={18} className="text-blue-600" />
                )}
              </div>
              <div>
                <div className="flex items-center">
                  <span className="font-medium">
                    {hasGames ? 'Add more games' : 'Add your first game'}
                  </span>
                  {hasGames && <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Completed</span>}
                </div>
                <p className="text-sm text-gray-600 mt-0.5">
                  {hasGames 
                    ? 'Keep adding games to see more detailed stats and trends.' 
                    : 'Record your game statistics to start tracking your performance.'}
                </p>
                {!hasGames && (
                  <Link 
                    to="/add-game" 
                    className="mt-1 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    Add Game <ChevronDown size={14} className="ml-1 transform -rotate-90" />
                  </Link>
                )}
              </div>
            </div>
            
            {/* Schedule */}
            <div className="flex items-start">
              <div className={`flex-shrink-0 rounded-full p-1 ${hasSchedule ? 'bg-green-100' : 'bg-blue-100'} mr-3`}>
                {hasSchedule ? (
                  <CheckCircle size={18} className="text-green-600" />
                ) : (
                  <Calendar size={18} className="text-blue-600" />
                )}
              </div>
              <div>
                <div className="flex items-center">
                  <span className="font-medium">
                    {hasSchedule ? 'Manage your schedule' : 'Add upcoming games'}
                  </span>
                  {hasSchedule && <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Completed</span>}
                </div>
                <p className="text-sm text-gray-600 mt-0.5">
                  {hasSchedule 
                    ? 'Keep your schedule up to date with upcoming games and practices.' 
                    : 'Add your upcoming games to the schedule to stay organized.'}
                </p>
                {!hasSchedule && (
                  <Link 
                    to="/schedule" 
                    className="mt-1 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    View Schedule <ChevronDown size={14} className="ml-1 transform -rotate-90" />
                  </Link>
                )}
              </div>
            </div>
            
            {/* Milestones */}
            <div className="flex items-start">
              <div className={`flex-shrink-0 rounded-full p-1 ${hasMilestones ? 'bg-green-100' : 'bg-blue-100'} mr-3`}>
                {hasMilestones ? (
                  <CheckCircle size={18} className="text-green-600" />
                ) : (
                  <Award size={18} className="text-blue-600" />
                )}
              </div>
              <div>
                <div className="flex items-center">
                  <span className="font-medium">
                    {hasMilestones ? 'Track your milestones' : 'Set performance goals'}
                  </span>
                  {hasMilestones && <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Completed</span>}
                </div>
                <p className="text-sm text-gray-600 mt-0.5">
                  {hasMilestones 
                    ? 'Keep working towards your milestones and set new goals.' 
                    : 'Create milestones to track your progress and celebrate achievements.'}
                </p>
                {!hasMilestones && (
                  <Link 
                    to="/milestones" 
                    className="mt-1 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    Set Milestones <ChevronDown size={14} className="ml-1 transform -rotate-90" />
                  </Link>
                )}
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 p-3 rounded-md mt-4">
            <p className="text-sm text-blue-800">
              <strong>Pro Tip:</strong> Add at least 3 games to see performance trends and analysis on your dashboard.
            </p>
          </div>
        </div>
      )}
    </StatCard>
  );
};

export default GettingStartedCard;
